// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// CareRx Enrollment queue — TanStack Query hooks (E-02). D-CRX-84 round 2: the queue's rows carry their
// server-computed stage + bucket, and two row actions run the REAL workflow steps (board Q5, option c):
// "Send to labs" (Clinical Review's send-back) and "Re-check labs" (the Lab Tracker recompute). Neither
// writes enrollment_status itself; both are audited server-side.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { changeTherapyLabRequirement, getTherapyLabs, listCareRxEnrollmentQueue, recomputeLabViolation, sendCareRxTherapyToLabs } from '../../api/client'
import { clinicalKeys } from './carerxClinical'
import { labTrackerKeys } from './carerxLabTracker'

export const enrollmentQueueKeys = {
  queue: ['carerx', 'enrollment-queue'] as const,
}

export function useEnrollmentQueue() {
  return useQuery({ queryKey: enrollmentQueueKeys.queue, queryFn: () => listCareRxEnrollmentQueue() })
}

/** A row action moved a therapy between lanes: the queue, the lane counts and the tracker are all stale. */
function useInvalidateLanes() {
  const qc = useQueryClient()
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: enrollmentQueueKeys.queue }),
    qc.invalidateQueries({ queryKey: clinicalKeys.counts }),
    qc.invalidateQueries({ queryKey: labTrackerKeys.list }),
  ])
}

/** D-CRX-95: an enrollment's own lab list (reader on). */
export const therapyLabKeys = {
  list: (therapyId: string) => ['carerx', 'therapy-labs', therapyId] as const,
}

/** Read through the cache so a list change below invalidates what the form shows (review F7). */
export function fetchTherapyLabs(qc: ReturnType<typeof useQueryClient>, therapyId: string) {
  return qc.fetchQuery({ queryKey: therapyLabKeys.list(therapyId), queryFn: () => getTherapyLabs(therapyId), staleTime: 0 })
}

/**
 * D-CRX-95: add / waive / restore one lab on an existing enrollment. The server re-checks the labs, so the
 * enrollment can move between the Lab Tracker and Clinical Review — the lanes refresh with the list (review F7).
 */
export function useChangeTherapyLab() {
  const qc = useQueryClient()
  const lanes = useInvalidateLanes()
  return useMutation({
    mutationFn: (v: { therapyId: string; body: Parameters<typeof changeTherapyLabRequirement>[1] }) =>
      changeTherapyLabRequirement(v.therapyId, v.body),
    onSuccess: (_r, v) => Promise.all([lanes(), qc.invalidateQueries({ queryKey: therapyLabKeys.list(v.therapyId) })]),
  })
}

type SendToLabsResult = Awaited<ReturnType<typeof sendCareRxTherapyToLabs>>
type RecheckResult = Awaited<ReturnType<typeof recomputeLabViolation>>

/** Outcome callbacks live at the HOOK level, not on mutate(): a successful action moves the row to another
 *  chip, the row unmounts, and TanStack skips mutate()-level callbacks once the caller has unmounted. They run
 *  BEFORE the invalidation for the same reason (Lucas, R2 review). */
export interface LaneActionCallbacks<R> {
  onSuccess?: (result: R, therapyId: string) => void
  onError?: (error: unknown, therapyId: string) => void
}

export function useSendToLabs(cb: LaneActionCallbacks<SendToLabsResult> = {}) {
  const invalidate = useInvalidateLanes()
  return useMutation({
    mutationFn: (therapyId: string) => sendCareRxTherapyToLabs(therapyId, 'enrollment_queue'),
    onSuccess: (r, therapyId) => { cb.onSuccess?.(r, therapyId); return invalidate() },
    onError: (e, therapyId) => cb.onError?.(e, therapyId),
  })
}

/** Re-runs the therapy's lab check from what is on file (no answers sent, so nothing is cleared). */
export function useRecheckLabs(cb: LaneActionCallbacks<RecheckResult> = {}) {
  const invalidate = useInvalidateLanes()
  return useMutation({
    mutationFn: (therapyId: string) => recomputeLabViolation(therapyId, []),
    onSuccess: (r, therapyId) => { cb.onSuccess?.(r, therapyId); return invalidate() },
    onError: (e, therapyId) => cb.onError?.(e, therapyId),
  })
}
